<script>
  // One drop, kept small: a name, a launch date and time, and the products that go on sale then. The detailed planning (steps, owners,
  // suppliers) happens elsewhere. Changes save themselves; the server answers with the whole drop.
  import Button from '../ui/Button.svelte';
  import Card from '../ui/Card.svelte';
  import Badge from '../ui/Badge.svelte';
  import Sheet from '../ui/Sheet.svelte';
  import Switch from '../ui/Switch.svelte';
  import DatePicker from '../ui/DatePicker.svelte';
  import TimePicker from '../ui/TimePicker.svelte';
  import ImageUpload from './ImageUpload.svelte';
  import VideoUpload from './VideoUpload.svelte';
  import Image from 'lucide-svelte/icons/image';
  import { inputCls, textareaCls, selectCls, labelCls, hintCls } from '../../lib/ui.js';
  import { toast } from '../../lib/toast.js';
  import { confirmDialog } from '../../lib/confirm.js';
  import { getOptimizedUrl } from '../../lib/cloudinary.js';
  import { DROP_STATUS, dropApi, fmtDate, fmtShort, launchText } from '../../lib/drops.js';
  import ArrowLeft from 'lucide-svelte/icons/arrow-left';
  import Trash2 from 'lucide-svelte/icons/trash-2';
  import Plus from 'lucide-svelte/icons/plus';
  import Settings2 from 'lucide-svelte/icons/settings-2';
  import CalendarPlus from 'lucide-svelte/icons/calendar-plus';
  import Package from 'lucide-svelte/icons/package';
  import Rocket from 'lucide-svelte/icons/rocket';
  import LoaderCircle from 'lucide-svelte/icons/loader-circle';

  let { drop, products = [], currency = 'R', onChange = () => {}, onBack = () => {}, onDeleted = () => {}, onProductsChanged = () => {} } = $props();

  let busy = $state('');
  async function run(name, fn) {
    if (busy) return null;
    busy = name;
    try { return await fn(); }
    catch (e) { toast.error(e.message); return null; }
    finally { busy = ''; }
  }
  const call = async (method, path, body) => { const v = await dropApi(method, `/api/drops/${drop.id}${path}`, body); onChange(v); return v; };

  const closed = $derived(['wrapped', 'cancelled'].includes(drop.status));
  const prelaunch = $derived(['planning', 'in_production', 'ready'].includes(drop.status));
  const isLive = $derived(drop.status === 'live');
  const when = $derived(`${fmtDate(drop.launchDate)} at ${drop.launchTime || '09:00'}`);

  function saidSync(v) {
    const sync = v?.sync;
    if (!sync) return;
    if (sync.released) toast.success(`${sync.released} product${sync.released === 1 ? ' is' : 's are'} on sale now.`);
    if (sync.held || sync.released) onProductsChanged();
  }
  const setStatus = (status) => run('status', async () => {
    const held = products.filter(p => p.publishAtDropId === drop.id && p.publishAt && new Date(p.publishAt) > new Date());
    if (status === 'cancelled' && held.length && !(await confirmDialog.ask({ title: 'Cancel this drop?', description: `${held.length} product${held.length === 1 ? ' is' : 's are'} waiting for this launch. Cancelling returns ${held.length === 1 ? 'it' : 'them'} to hidden drafts.`, confirmLabel: 'Cancel the drop', cancelLabel: 'Keep it', destructive: true }))) return;
    if (status === 'live' && held.length && !(await confirmDialog.ask({ title: 'Launch now?', description: `${held.length} product${held.length === 1 ? '' : 's'} go${held.length === 1 ? 'es' : ''} on sale immediately instead of waiting for ${fmtDate(drop.launchDate)}.`, confirmLabel: 'Launch now', cancelLabel: 'Not yet' }))) return;
    saidSync(await call('PUT', '', { status }));
  });
  const deleteDrop = () => run('del', async () => {
    if (!(await confirmDialog.ask({ title: `Delete “${drop.name}”?`, description: 'The drop is removed. Products in your store are not deleted; any held for this launch go back to hidden drafts.', confirmLabel: 'Delete drop', destructive: true }))) return;
    await dropApi('DELETE', `/api/drops/${drop.id}`);
    detOpen = false;
    onDeleted(drop.id);
    onProductsChanged();
    toast.success('Drop deleted');
  });

  // ── Details: edited freely, saved with the button ───────────────────────────────
  let detOpen = $state(false);
  let det = $state(null);
  let detSaved = $state('');
  let detBusy = $state(false);
  const detFields = (d) => ({ name: d.name, notes: d.notes, launchDate: d.launchDate, launchTime: d.launchTime, featureOnLaunch: Boolean(d.featureOnLaunch) });
  function openDetails() {
    det = { name: drop.name, notes: drop.notes || '', launchDate: drop.launchDate, launchTime: drop.launchTime || '09:00', featureOnLaunch: Boolean(drop.featureOnLaunch) };
    detSaved = JSON.stringify(detFields(det)); detOpen = true;
  }
  const detDirty = $derived(Boolean(det) && JSON.stringify(detFields(det)) !== detSaved);
  const detValid = $derived(Boolean(det) && det.name.trim() && /^\d\d:\d\d$/.test(det.launchTime) && Boolean(det.launchDate));
  async function saveDet() {
    if (detBusy || !detDirty || !detValid) return;
    const snap = JSON.stringify(detFields(det));
    const now = JSON.parse(snap), was = JSON.parse(detSaved);
    detBusy = true;
    try {
      const moved = now.launchDate !== was.launchDate || now.launchTime !== was.launchTime;
      const at = { launchDate: now.launchDate, launchTime: now.launchTime };
      // A live drop moved to a moment still ahead goes back to scheduled, and its products come off sale until then: ask first.
      if (moved && isLive) {
        const plan = await dropApi('POST', `/api/drops/${drop.id}/reschedule`, { ...at, dryRun: true });
        if (plan.relaunch && !(await confirmDialog.ask({ title: 'Take this drop off sale until then?', description: `${plan.onSale} product${plan.onSale === 1 ? '' : 's'} come${plan.onSale === 1 ? 's' : ''} off sale now and go${plan.onSale === 1 ? 'es' : ''} on sale again on ${fmtDate(now.launchDate)} at ${now.launchTime}.`, confirmLabel: 'Take off sale', cancelLabel: 'Go back', destructive: true }))) return;
      }
      const body = {};
      for (const k of ['name', 'notes', 'featureOnLaunch']) if (now[k] !== was[k]) body[k] = now[k];
      if (Object.keys(body).length) await call('PUT', '', body);
      const v = moved ? await call('POST', '/reschedule', at) : null;
      detSaved = snap; detOpen = false;
      toast.success(v?.relaunch ? `Scheduled again for ${fmtDate(now.launchDate)} at ${now.launchTime}. Its products are off sale until then.` : moved ? 'Saved. Its products follow the new date and time.' : 'Saved');
      if (moved) onProductsChanged();
    } catch (e) { toast.error(e.message); }
    finally { detBusy = false; }
  }

  // ── Its own homepage hero, shown while the drop is live ────────────────────────
  let heroOpen = $state(false);
  let hero = $state(null);
  let heroSaved = $state('');
  let heroBusy = $state(false);
  const blankHero = { enabled: true, label: '', heading: '', subheading: '', cta: 'Shop the drop', ctaLink: '', image: '', video: '' };
  function openHero() {
    hero = drop.hero?.enabled || drop.hero?.heading || drop.hero?.image ? { ...blankHero, ...drop.hero } : { ...blankHero, heading: drop.name };
    heroSaved = JSON.stringify(hero); heroOpen = true;
  }
  const heroDirty = $derived(Boolean(hero) && JSON.stringify(hero) !== heroSaved);
  async function saveHero() {
    if (heroBusy || !heroDirty) return;
    heroBusy = true;
    try { await call('PUT', '', { hero }); heroSaved = JSON.stringify(hero); heroOpen = false; toast.success(hero.enabled ? (isLive ? 'The homepage now shows this hero.' : 'Saved. The homepage shows it from launch.') : 'Saved. The homepage keeps its usual hero.'); }
    catch (e) { toast.error(e.message); }
    finally { heroBusy = false; }
  }

  // ── Products in the drop ────────────────────────────────────────────────────────
  const productOf = (id) => products.find(p => p.id === id);
  const inDrop = $derived(new Set(drop.styles.map(s => s.productId).filter(Boolean)));
  const addable = $derived(products.filter(p => !inDrop.has(p.id)));
  let addOpen = $state(false);
  let addPicked = $state([]);
  let addQuery = $state('');
  const addChoices = $derived([...addable].filter(p => !addQuery.trim() || p.name.toLowerCase().includes(addQuery.trim().toLowerCase())).sort((x, y) => Number(Boolean(y.hidden)) - Number(Boolean(x.hidden))));
  const heldElsewhere = (p) => Boolean(p.publishAtDropId) && p.publishAtDropId !== drop.id;
  function openAdd() { addPicked = []; addQuery = ''; addOpen = true; }
  async function addProducts() {
    const ids = addPicked;
    if (!ids.length) return;
    const live = ids.map(productOf).filter(p => p && !p.hidden && !p.publishAtDropId);
    if (isLive && !(await confirmDialog.ask({ title: 'Put them on sale now?', description: `This drop is live, so ${ids.length === 1 ? 'it goes' : `all ${ids.length} go`} on sale straight away, tagged New${drop.featureOnLaunch ? ' and featured on the homepage' : ''}.`, confirmLabel: 'Add and put on sale', cancelLabel: 'Go back' }))) return;
    if (!isLive && live.length && !(await confirmDialog.ask({ title: `${live.length} product${live.length === 1 ? ' is' : 's are'} on sale right now`, description: 'Adding to this drop hides them from the shop until it launches.', confirmLabel: 'Hide and add', cancelLabel: 'Go back' }))) return;
    await run('add', async () => {
      saidSync(await call('POST', '/products', { productIds: ids }));
      addOpen = false;
      toast.success(`${ids.length} product${ids.length === 1 ? '' : 's'} added.`);
      onProductsChanged();
    });
  }
  // Show the switch at once; the server confirms in the background.
  let chain = Promise.resolve();
  function setSoon(s, showSoon) {
    onChange({ ...drop, styles: drop.styles.map(x => (x.id === s.id ? { ...x, showSoon } : x)) });
    chain = chain.then(() => dropApi('PUT', `/api/drops/${drop.id}/styles/${s.id}`, { showSoon })).then((v) => { onChange(v); onProductsChanged(); })
      .catch(async (e) => { toast.error(e.message); try { onChange(await dropApi('GET', `/api/drops/${drop.id}`)); } catch { /* keep what is shown */ } });
  }
  const removeStyle = (s) => run('rm', async () => {
    if (!(await confirmDialog.ask({ title: `Remove “${s.name}” from this drop?`, description: s.productId ? 'The product stays in your store as a hidden draft.' : 'Nothing else changes.', confirmLabel: 'Remove', destructive: true }))) return;
    saidSync(await call('DELETE', `/styles/${s.id}`));
  });
  function shopState(s) {
    if (!s.productId) return 'Not in your store';
    if (!prelaunch) return 'On sale';
    return s.showSoon ? 'Listed as Coming soon' : 'Hidden from the shop';
  }
</script>

<div class="space-y-6">
  <div class="flex flex-wrap items-start justify-between gap-3">
    <div class="min-w-0 space-y-2">
      <button type="button" onclick={onBack} class="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={15} /> All drops</button>
      <div class="flex flex-wrap items-center gap-3">
        <h1 class="text-2xl font-semibold tracking-tight">{drop.name}</h1>
        <Badge variant="outline">{prelaunch ? 'Scheduled' : DROP_STATUS[drop.status]}</Badge>
      </div>
      <p class="text-sm text-muted-foreground">{prelaunch ? 'Goes on sale' : 'Launched'} <span class="font-medium text-foreground">{when}</span>{#if prelaunch} · {launchText(drop.daysToLaunch)}{/if}{#if prelaunch && drop.featureOnLaunch} · featured on the homepage at launch{/if}</p>
      {#if drop.notes}<p class="max-w-prose whitespace-pre-line text-sm text-muted-foreground">{drop.notes}</p>{/if}
    </div>
    <div class="flex flex-wrap items-center gap-2">
      {#if prelaunch}<Button onclick={() => setStatus('live')} disabled={busy === 'status'}><Rocket size={16} /> Launch now</Button>
      {:else if drop.status === 'live'}<Button variant="outline" onclick={() => setStatus('wrapped')} disabled={busy === 'status'}>Wrap up</Button>{/if}
      <Button variant="outline" onclick={openDetails}><Settings2 size={16} /> Edit</Button>
    </div>
  </div>

  {#if prelaunch || isLive}
    <Card>
      <div class="space-y-3 p-4 md:p-6">
        <div class="flex flex-wrap items-center gap-3">
          <Button onclick={openAdd} disabled={!addable.length}><Plus size={16} /> Add products</Button>
          {#if !addable.length}<span class="text-sm text-muted-foreground">{products.length ? 'All your products are in this drop.' : 'No products in your store yet.'}</span>{/if}
        </div>
        {#if isLive}
          <p class="text-sm text-muted-foreground">This drop is live: products you add go on sale straight away, tagged New.</p>
        {:else}
          <p class="text-sm text-muted-foreground">Products in a drop are hidden from the shop and go on sale, tagged New, at {drop.launchDate ? when : 'launch'}. Turn on <span class="font-medium text-foreground">Coming soon</span> to show one early without letting anyone buy it.</p>
        {/if}
      </div>
    </Card>
  {/if}

  {#if !closed || drop.hero?.enabled}
    <Card>
      <div class="flex flex-wrap items-center gap-4 p-4 md:p-6">
        <div class="h-14 w-24 shrink-0 overflow-hidden rounded-md bg-muted">{#if drop.hero?.image}<img src={getOptimizedUrl(drop.hero.image, 192)} alt="" class="h-full w-full object-cover" loading="lazy" />{:else}<div class="grid h-full place-items-center text-muted-foreground"><Image size={18} /></div>{/if}</div>
        <div class="min-w-0 flex-1 basis-48">
          <p class="text-sm font-medium">Homepage hero</p>
          <p class="text-sm text-muted-foreground">{#if drop.hero?.enabled}{isLive ? 'Showing now' : closed ? 'Was shown while the drop was live' : 'Shown from launch'}: “{drop.hero.heading || drop.name}”{:else}The homepage keeps its usual hero. Give this drop its own, shown while it is live.{/if}</p>
        </div>
        {#if !closed}<Button variant="outline" onclick={openHero}>{drop.hero?.enabled ? 'Edit hero' : 'Set up a hero'}</Button>{/if}
      </div>
    </Card>
  {/if}

  {#if drop.styles.length === 0}
    <Card><div class="flex flex-col items-center gap-2 py-12 text-center"><Package size={28} class="text-muted-foreground" /><p class="font-medium">No products in this drop yet</p><p class="max-w-md text-sm text-muted-foreground">Create products in Products first (as hidden drafts if you like), then add them here.</p></div></Card>
  {:else}
    <Card class="overflow-hidden">
      <ul>
        {#each drop.styles as s (s.id)}
          {@const p = productOf(s.productId)}
          <li class="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-border/60 px-4 py-3 last:border-0">
            <div class="h-12 w-12 shrink-0 overflow-hidden rounded-md bg-muted">{#if p?.image || p?.images?.[0]}<img src={getOptimizedUrl(p.image || p.images[0], 96)} alt="" class="h-full w-full object-cover" loading="lazy" />{/if}</div>
            <div class="min-w-0 flex-1 basis-48">
              <p class="truncate text-sm font-medium">{s.name}</p>
              <p class="text-xs text-muted-foreground">{shopState(s)}{#if s.plannedPrice}{` · ${currency}${Number(s.plannedPrice).toFixed(2)}`}{/if}</p>
            </div>
            {#if prelaunch}
              <label class="flex items-center gap-2 text-sm" title={s.productId ? 'List it in the shop with a Coming soon tag' : 'Not in your store'}>
                <Switch checked={s.showSoon} disabled={!s.productId} aria-label="List {s.name} as Coming soon" onclick={() => setSoon(s, !s.showSoon)} />
                <span class={s.productId ? '' : 'text-muted-foreground'}>Coming soon</span>
              </label>
            {/if}
            <Button variant="ghost" size="icon" aria-label="Remove {s.name}" class="hover:text-destructive" onclick={() => removeStyle(s)}><Trash2 size={15} /></Button>
          </li>
        {/each}
      </ul>
    </Card>
  {/if}
</div>

<Sheet bind:open={addOpen} title="Add products" description={isLive ? 'They go on sale as soon as you add them. Drafts are listed first.' : `Hidden from the shop until ${when}. Drafts are listed first.`}>
  <div class="space-y-4">
    <input type="search" class={inputCls} placeholder="Search products" aria-label="Search products" bind:value={addQuery} />
    <div class="max-h-[55vh] overflow-y-auto rounded-md border border-border">
      {#each addChoices as p (p.id)}
        <label class="flex items-center gap-3 border-b border-border/60 px-3 py-2 text-sm last:border-0 hover:bg-muted/40 {heldElsewhere(p) ? 'opacity-60' : ''}">
          <input type="checkbox" class="h-4 w-4 rounded border-input" value={p.id} bind:group={addPicked} disabled={heldElsewhere(p)} />
          <span class="min-w-0 flex-1 truncate">{p.name}</span>
          <span class="shrink-0 text-xs text-muted-foreground">{heldElsewhere(p) ? 'In another drop' : p.hidden ? 'Draft' : 'On sale'}</span>
        </label>
      {/each}
      {#if addChoices.length === 0}<p class="px-3 py-6 text-center text-sm text-muted-foreground">No matching products.</p>{/if}
    </div>
    <div class="flex justify-end gap-2"><Button variant="outline" onclick={() => (addOpen = false)}>Cancel</Button><Button onclick={addProducts} disabled={!addPicked.length || busy === 'add'}>{#if busy === 'add'}<LoaderCircle size={14} class="animate-spin" />{/if} Add {addPicked.length || ''} product{addPicked.length === 1 ? '' : 's'}</Button></div>
  </div>
</Sheet>

<Sheet bind:open={heroOpen} title="Homepage hero" description="Shown in place of your usual hero while this drop is live. When the drop is wrapped up, the usual one comes back.">
  {#if hero}
    <div class="space-y-5">
      <div class="flex items-center justify-between gap-4">
        <div class="text-sm"><p class="font-medium">Use this hero while the drop is live</p><p class="text-muted-foreground">Off keeps your usual hero.</p></div>
        <Switch bind:checked={hero.enabled} aria-label="Use this hero while the drop is live" />
      </div>
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div><label class={labelCls} for="hr-label">Small label <span class="font-normal text-muted-foreground">(optional)</span></label><input id="hr-label" class={inputCls} bind:value={hero.label} maxlength="60" placeholder="e.g. Out now" /></div>
        <div><label class={labelCls} for="hr-heading">Title</label><input id="hr-heading" class={inputCls} bind:value={hero.heading} maxlength="80" /></div>
      </div>
      <div><label class={labelCls} for="hr-sub">Subtitle <span class="font-normal text-muted-foreground">(optional)</span></label><input id="hr-sub" class={inputCls} bind:value={hero.subheading} maxlength="200" /></div>
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div><label class={labelCls} for="hr-cta">Button text</label><input id="hr-cta" class={inputCls} bind:value={hero.cta} maxlength="40" /></div>
        <div><label class={labelCls} for="hr-link">Button link</label><input id="hr-link" class={inputCls} bind:value={hero.ctaLink} maxlength="300" placeholder="/shop?filter=new" /><p class={hintCls}>Empty: your New arrivals.</p></div>
      </div>
      <ImageUpload label="Background image" value={hero.image} onChange={(url) => (hero.image = url)} />
      <VideoUpload label="Background video / GIF (optional)" value={hero.video} stripAudio onChange={(url) => (hero.video = url)} />
      <div class="flex justify-end gap-2 border-t border-border pt-4">
        <Button variant="outline" onclick={() => (heroOpen = false)} disabled={heroBusy}>Cancel</Button>
        <Button onclick={saveHero} disabled={heroBusy || !heroDirty}>{#if heroBusy}<LoaderCircle size={14} class="animate-spin" />{/if} Save changes</Button>
      </div>
    </div>
  {/if}
</Sheet>

<Sheet bind:open={detOpen} title="Edit drop" description="Your changes are saved when you press Save changes.">
  {#if det}
    <div class="space-y-5">
      <div><label class={labelCls} for="dt-name">Name</label><input id="dt-name" class={inputCls} bind:value={det.name} maxlength="80" required /></div>
      <div class="grid grid-cols-2 gap-4">
        <div><label class={labelCls} for="dt-date">Date</label><DatePicker id="dt-date" bind:value={det.launchDate} min={prelaunch ? drop.today : ''} disabled={closed} /></div>
        <div><label class={labelCls} for="dt-time">Time</label><TimePicker id="dt-time" bind:value={det.launchTime} disabled={closed} /></div>
      </div>
      <p class={hintCls}>{isLive ? 'Shop time. Moving it to a moment still ahead takes its products off sale until then; an earlier moment only corrects the record.' : 'Shop time. The products go on sale by themselves then.'}</p>
      <div class="flex items-center justify-between gap-4">
        <div class="text-sm"><p class="font-medium">Feature on the homepage when it launches</p><p class="text-muted-foreground">Its products lead the homepage's featured strip from launch time.</p></div>
        <Switch bind:checked={det.featureOnLaunch} disabled={closed} aria-label="Feature on the homepage when it launches" />
      </div>
      <div><label class={labelCls} for="dt-notes">Notes <span class="font-normal text-muted-foreground">(optional)</span></label><textarea id="dt-notes" class={textareaCls} bind:value={det.notes} maxlength="2000" rows="3" placeholder="Theme, story, a link to your plan"></textarea></div>
      <div class="flex items-center justify-between gap-2 pt-2">
        <a href="/api/drops/{drop.id}/calendar.ics" download="{drop.name}.ics" class="inline-flex h-9 items-center gap-2 rounded-md px-3 text-sm font-medium hover:bg-accent"><CalendarPlus size={14} /> Add to calendar</a>
      </div>
      <div class="flex gap-1 border-t border-border pt-4">
        <Button variant="ghost" class="text-destructive hover:text-destructive" onclick={deleteDrop} disabled={!!busy}><Trash2 size={14} /> Delete</Button>
        {#if prelaunch}<Button variant="ghost" onclick={() => { detOpen = false; setStatus('cancelled'); }} disabled={!!busy}>Cancel drop</Button>{/if}
      </div>
      <div class="flex justify-end gap-2 border-t border-border pt-4">
        <Button variant="outline" onclick={() => (detOpen = false)} disabled={detBusy}>Cancel</Button>
        <Button onclick={saveDet} disabled={detBusy || !detDirty || !detValid}>{#if detBusy}<LoaderCircle size={14} class="animate-spin" />{/if} Save changes</Button>
      </div>
    </div>
  {/if}
</Sheet>
